import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import ProfileActionCard, { ProfileActionCardProps, Action as PatientAction } from '@/components/custom/ProfileActionCard/ProfileActionCard';
import { LucideProps, Calendar, DollarSign, Shield, Gift, AlertTriangle, User, ChevronRight, Clock, Phone, MessageCircle, Mail, Video } from 'lucide-react';

// --- TYPE DEFINITIONS ---

export interface PatientItemData {
  id: string;
  title: string;
  subtitle?: string;
  status?: string;
  date?: string;
  value?: string;
  metadata?: Record<string, any>;
}

export interface PatientSection {
  title: string;
  type?: string;
  items: PatientItemData[];
  maxItems?: number;
  showViewAll?: boolean;
  summary?: string;
}

export interface Patient {
  id: string;
  name: string;
  avatar?: string;
  details?: string;
  initials?: string;
  age?: string | number;
  height?: string;
  gender?: string;
  phone?: string;
  actions?: PatientAction[];
  onActionClick?: (action: PatientAction) => void;
  selectedActions?: string[];
}

export type SectionIconMap = { [key: string]: React.ComponentType<LucideProps> };

export const SECTION_ICONS: SectionIconMap = {
  appointments: Calendar,
  balance: DollarSign,
  insurance: Shield,
  benefits: Gift,
  alerts: AlertTriangle,
  referrals: User,
  'recalls & reviews': Calendar,
  'risk & alerts': AlertTriangle,
  default: User,
};

// --- SUB-COMPONENTS ---

const getStatusVariant = (status?: string) => {
  switch (status) {
    case 'confirmed': case 'paid': case 'active': return 'success';
    case 'pending': case 'partial': case 'pending_verification': case 'medium': return 'warning';
    case 'cancelled': case 'overdue': case 'expired': case 'high': return 'destructive';
    case 'scheduled': case 'low': return 'info';
    default: return 'default';
  }
};

interface PatientItemProps {
  item: PatientItemData;
  type?: string;
  onClick?: (item: PatientItemData) => void;
  className?: string;
  sectionIcons?: SectionIconMap;
}

const PatientItem = React.forwardRef<HTMLDivElement, PatientItemProps>((
  { item, type = 'default', onClick, className = '', sectionIcons = {} },
  ref
) => {
  const IconComponent = sectionIcons[type] || null;
  const handleClick = () => onClick?.(item);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(item);
    }
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn('flex items-center justify-between p-4 rounded-lg transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500', className)}
    >
      <div className="flex items-center gap-4">
        {IconComponent && <IconComponent className="h-6 w-6 text-gray-500" />}
        <div>
          <p className="font-semibold text-gray-800">{item.title}</p>
          {item.subtitle && <p className="text-sm text-gray-500">{item.subtitle}</p>}
          {(item.date || item.value) && (
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              {item.date && <div className="flex items-center gap-1"><Clock className="h-3 w-3" /><span>{item.date}</span></div>}
              {item.value && <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" /><span>{item.value}</span></div>}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 ml-4">
        {item.status && <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>}
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  );
});
PatientItem.displayName = 'PatientItem';

interface AccordionPatientSectionProps {
  items: PatientItemData[];
  type?: string;
  maxItems?: number;
  showViewAll?: boolean;
  sectionTitle: string;
  onViewAll?: (title: string, items: PatientItemData[]) => void;
  onItemClick?: (item: PatientItemData) => void;
  className?: string;
  sectionIcons?: SectionIconMap;
}

const AccordionPatientSection = React.forwardRef<HTMLDivElement, AccordionPatientSectionProps>((
  { items, type, maxItems = 3, showViewAll = true, sectionTitle, onViewAll, onItemClick, className = '', sectionIcons = {} },
  ref
) => {
  const displayedItems = items.slice(0, maxItems);
  const hasMoreItems = items.length > maxItems;

  return (
    <div ref={ref} className={cn('px-4 pb-4 md:px-6', className)}>
      <div className="space-y-3">
        {displayedItems.map((item) => (
          <PatientItem key={item.id} item={item} type={type} onClick={onItemClick} sectionIcons={sectionIcons} />
        ))}
      </div>
      {hasMoreItems && showViewAll && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewAll?.(sectionTitle, items)}
          className="w-full mt-4 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
        >
          View all {items.length} items
        </Button>
      )}
    </div>
  );
});
AccordionPatientSection.displayName = 'AccordionPatientSection';

// --- MAIN COMPONENT ---

export interface PatientCardProps {
  patient?: Patient;
  sections: PatientSection[];
  title?: string;
  type?: 'single' | 'multiple';
  collapsible?: boolean;
  defaultValue?: string | string[];
  maxItemsPerSection?: number;
  showViewAll?: boolean;
  onSectionViewAll?: (title: string, items: PatientItemData[]) => void;
  onItemClick?: (item: PatientItemData) => void;
  className?: string;
}

const PatientCard = React.forwardRef<HTMLDivElement, PatientCardProps>((
  { patient, sections, title, type = 'multiple', collapsible = true, defaultValue, maxItemsPerSection, showViewAll, onSectionViewAll, onItemClick, className = '' },
  ref
) => {
  const validSections = sections.filter(s => s && s.title && Array.isArray(s.items));
  if (validSections.length === 0) return null;

  const accordionDefaultValue = defaultValue || (type === 'multiple' ? [validSections[0]?.title] : validSections[0]?.title);

  return (
    <div ref={ref} className={cn('w-full bg-white border border-gray-200 shadow-sm rounded-xl', className)} role="region" aria-label={title || patient?.name || 'Patient Information'}>
      {patient && (
        <div className="p-4 md:p-6 pb-4 border-b border-gray-100">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={patient.avatar} alt={patient.name} />
                <AvatarFallback className="bg-sky-100 text-sky-700 font-semibold">
                  {patient.initials || patient.name?.split(' ').map(n => n[0]).join('') || 'P'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-gray-900 truncate">{patient.name}</h2>
                <p className="text-sm text-gray-600 truncate">{patient.details}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {patient.age && <div><span className="font-semibold">Age:</span> {patient.age}</div>}
              {patient.height && <div><span className="font-semibold">Height:</span> {patient.height}</div>}
              {patient.gender && <div><span className="font-semibold">Gender:</span> {patient.gender}</div>}
              {patient.phone && <div><span className="font-semibold">Phone:</span> {patient.phone}</div>}
            </div>
            {patient.actions && (
              <div className="hidden md:block">
                <ProfileActionCard actions={patient.actions} onActionClick={patient.onActionClick} selectedActions={patient.selectedActions} />
              </div>
            )}
            {patient.actions && (
              <div className="flex items-center justify-center gap-3 md:hidden">
                {patient.actions.slice(0, 4).map((action) => {
                  const IconComponent = action.icon;
                  return (
                    <Button
                      key={action.id}
                      variant={action.variant || 'ghost'}
                      size="sm"
                      onClick={() => patient.onActionClick?.(action)}
                      className="h-12 w-12 rounded-full p-0"
                      aria-label={action.label}
                      title={action.label}
                    >
                      <IconComponent className="h-5 w-5" />
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="p-2 md:p-4">
        <Accordion type={type} collapsible={collapsible} defaultValue={accordionDefaultValue} className="w-full">
          {validSections.map((section) => {
            const IconComponent = SECTION_ICONS[section.title.toLowerCase()] || SECTION_ICONS.default;
            return (
              <AccordionItem key={section.title} value={section.title} className="border border-gray-200 bg-white shadow-sm mb-2 rounded-lg">
                <AccordionTrigger className="px-4 py-3 md:px-6 md:py-4 hover:no-underline hover:bg-gray-50">
                  <div className="flex items-center justify-between w-full mr-4">
                    <div className="flex items-center gap-3">
                      <IconComponent className="h-5 w-5 text-gray-600" />
                      <h3 className="text-lead font-semibold text-gray-900 text-left">{section.title}</h3>
                    </div>
                    <div className="flex items-center gap-4">
                      {section.summary && <span className="hidden md:block text-caption text-gray-600">{section.summary}</span>}
                      <span className="text-caption text-gray-500">{section.items.length} items</span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  {section.items.length > 0 ? (
                    <AccordionPatientSection
                      items={section.items}
                      type={section.type}
                      maxItems={section.maxItems || maxItemsPerSection}
                      showViewAll={section.showViewAll !== undefined ? section.showViewAll : showViewAll}
                      sectionTitle={section.title}
                      onViewAll={onSectionViewAll}
                      onItemClick={onItemClick}
                      sectionIcons={SECTION_ICONS}
                    />
                  ) : (
                    <div className="px-4 pb-4 md:px-6 pt-0 text-center py-4 text-sm text-gray-500">
                      No {section.title.toLowerCase()} available
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
});
PatientCard.displayName = 'PatientCard';

export default PatientCard;
